import React from 'react';
import Select from '../../../components/Select/Select';
import Input from '../../../components/Input/Input';
import InputText from '../../../components/Input/InputText';
import TextArea from '../../../components/TextArea/TextArea';
import Checkbox from '../../../components/Checkbox/Checkbox';
import GroupRadio from '../../../components/GroupRadio/GroupRadio';
import DealershipAccountField from '../../../components/DealershipAccountField/DealershipAccountField';

const MAKES_MAX_NUMBER = 4;

const renderMakeAndModels = (index, formik) => {
  if (!formik.values.vehicles[index]) {
    return null;
  }

  const { makeId, makeName, models } = formik.values.vehicles[index];

  return (
    <div className="grid gap-6 grid-cols-2" key={index}>
      <div className="max-w-[18rem]">
        <Select
          label={index === 0 ? 'Makes' : ''}
          id="makes"
          required={index === 0}
          value={makeId}
          disabled
          options={[{ value: makeId, label: makeName }]}
          dataTest={`generated-prompt-form-makes-${index}`}
          bottomMarginSizing={1}
        />
      </div>
      <div className="max-w-[18rem]">
        <Select
          label={index === 0 ? 'Models' : ''}
          id="models"
          value={models.map(({ modelId }) => modelId)}
          disabled
          options={models.map((model) => ({ value: model.modelId, label: model.modelName }))}
          dataTest={`generated-prompt-form-models-${index}`}
          bottomMarginSizing={1}
          isMulti={true}
        />
      </div>
    </div>
  );
};

const renderAllMakesAndModels = (formik) => {
  return Array.from({ length: MAKES_MAX_NUMBER }, (_v, i) => i).map((index) =>
    renderMakeAndModels(index, formik)
  );
};

const renderFilter = (label, key, formik) => {
  if (!formik.values.filter?.[key] || !formik.values.filter[key]?.length) {
    return null;
  }

  return (
    <div className="grid gap-6 grid-cols-1">
      <div className="max-w-[18rem]">
        <Select
          label={label}
          id={key}
          value={formik.values.filter[key]}
          disabled
          options={formik.values.filter[key].map((v) => ({ value: v, label: v }))}
          dataTest={`generated-prompt-form-${key}`}
          bottomMarginSizing={1}
          isMulti={true}
        />
      </div>
    </div>
  );
};

const InventoryPartial = ({ formik }) => {
  return (
    <>
      <div className="grid gap-6 grid-cols-2">
        <div className="-mt-2 max-w-[18rem]">
          <Select
            label="Year"
            id="year"
            required
            value={formik.values.year}
            disabled
            options={[{ value: formik.values.year, label: formik.values.year }]}
            dataTest="generated-prompt-form-year"
            bottomMarginSizing={1}
          />
        </div>
        <div className="mt-7 max-w-[18rem]">
          <GroupRadio
            defaultValue={formik.values.inventoryType}
            onChange={(value) => {
              formik.setFieldValue('inventoryType', value);
            }}
            dataTest="generated-prompt-form-inventoryType"
            items={[
              {
                label: 'New',
                value: 'new',
                id: 'inventoryType'
              },
              {
                label: 'Used',
                value: 'used',
                id: 'inventoryType'
              }
            ]}
            disabled
          />
        </div>
      </div>

      {renderAllMakesAndModels(formik)}

      {renderFilter('Body Styles', 'bodyStyles', formik)}
      {renderFilter('Sizes', 'sizes', formik)}
      {renderFilter('Fuel Types', 'fuelTypes', formik)}

      <div className="grid gap-6 grid-cols-2">
        <div className="max-w-[18rem]">
          <DealershipAccountField
            label="Dealership Account Id"
            id="dealershipAccount"
            value={formik.values?.dealershipAccount}
            disabled
            maxLength={50}
            dataTest="generated-prompt-form-dealership-account-id"
            bottomMarginSizing={1}
          />

          <TextArea
            label="Content Topic"
            id="contentTopic"
            value={formik.values?.contentTopic}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            required
            dataTest="generated-prompt-form-content-topic"
            disabled
            bottomMarginSizing={1}
          />

          <div className="grid grid-rows-1 grid-cols-form-word-copyscape gap-6 mb-4">
            <div className="row-start-1 row-end-2 col-start-1 col-end-2 w-full max-w-[128px]">
              <Input
                label="Word Count"
                value={formik.values?.wordCount}
                onChange={({ target: { value } }) => {
                  formik.setFieldValue('wordCount', value.replace(/[^\d]/, ''));
                }}
                onBlur={formik.handleBlur}
                id="wordCount"
                maxLength={4}
                required
                dataTest="generated-prompt-form-word-count"
                type="tel"
                max="2500"
                min="100"
                pattern="[0-9]{1,4}"
                disabled
              />
            </div>

            <div className="row-start-1 row-end-2 col-start-2 col-end-3 flex justify-end whitespace-nowrap">
              <Checkbox
                label="Check with Copyscape"
                value={formik.values?.executeCopyscapeSearch}
                onChange={({ target: { checked } }) => {
                  formik.setFieldValue('executeCopyscapeSearch', checked);
                }}
                onBlur={formik.handleBlur}
                type="checkbox"
                error={
                  formik.touched?.executeCopyscapeSearch && formik.errors?.executeCopyscapeSearch
                }
                id="copyscape"
                dataTest="mslp-form-copyscape"
              />
            </div>
          </div>

          <TextArea
            label="Target Audience or Market"
            id="targetAudience"
            value={formik.values?.targetAudience}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="generated-prompt-form-target-audience"
            error={formik.touched?.targetAudience && formik.errors?.targetAudience}
            bottomMarginSizing={1}
          />

          <TextArea
            label="Geos"
            id="geos"
            value={formik.values?.geos}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="generated-prompt-form-geos"
            error={formik.touched?.geos && formik.errors?.geos}
            bottomMarginSizing={0}
          />
        </div>

        <div className="mt-2 max-w-[18rem]">
          <TextArea
            label="Content Example 1"
            id="contentOne.content"
            value={formik.values?.contentOne?.content}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
            dataTest="generated-prompt-form-content-one-content"
            error={formik.touched?.contentOne?.content && formik.errors?.contentOne?.content}
            placeholder="Place the HTML copied content"
          />

          <InputText
            id="contentOne.writeStyle"
            value={formik.values?.contentOne.writeStyle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
            dataTest="generated-prompt-form-content-one-write-style"
            error={formik.touched?.contentOne?.writeStyle && formik.errors?.contentOne?.writeStyle}
            placeholder="What to take from example ex. tone, style, etc."
            bottomMarginSizing={1}
          />

          <TextArea
            label="Content Example 2"
            id="contentTwo.content"
            value={formik.values?.contentTwo?.content}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
            dataTest="generated-prompt-form-content-two-content"
            error={formik.touched?.contentTwo?.content && formik.errors?.contentTwo?.content}
            placeholder="Place the HTML copied content"
          />

          <InputText
            id="contentTwo.writeStyle"
            value={formik.values?.contentTwo.writeStyle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
            dataTest="generated-prompt-form-content-two-write-style"
            error={formik.touched?.contentTwo?.writeStyle && formik.errors?.contentTwo?.writeStyle}
            placeholder="What to take from example ex. tone, style, etc."
            bottomMarginSizing={1}
          />

          <TextArea
            label="Additional Information"
            id="additionalInformation"
            value={formik.values?.additionalInformation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="generated-prompt-form-additional-information"
            error={formik.touched?.additionalInformation && formik.errors?.additionalInformation}
            bottomMarginSizing={1}
          />

          <TextArea
            label="Negative Prompt"
            id="negativePrompt"
            value={formik.values?.negativePrompt}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="generated-prompt-form-negative-prompt"
            error={formik.touched?.negativePrompt && formik.errors?.negativePrompt}
            bottomMarginSizing={1}
          />

          <InputText
            label="Link 1"
            id="linkOne.url"
            value={formik.values?.linkOne.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="generated-prompt-form-link-one-url"
            error={formik.touched?.linkOne?.url && formik.errors?.linkOne?.url}
            placeholder="ex. https://www.dealer.com/"
          />

          <InputText
            id="linkOne.description"
            value={formik.values?.linkOne.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="generated-prompt-form-link-one-description"
            error={formik.touched?.linkOne?.description && formik.errors?.linkOne?.description}
            placeholder="Add link description"
            bottomMarginSizing={1}
          />

          <InputText
            label="Link 2"
            id="linkTwo.url"
            value={formik.values?.linkTwo.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="generated-prompt-form-link-two-url"
            error={formik.touched?.linkTwo?.url && formik.errors?.linkTwo?.url}
            placeholder="ex. https://www.dealer.com/"
          />

          <InputText
            id="linkTwo.description"
            value={formik.values?.linkTwo.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="generated-prompt-form-link-two-description"
            error={formik.touched?.linkTwo?.description && formik.errors?.linkTwo?.description}
            placeholder="Add link description"
            bottomMarginSizing={0}
          />
        </div>
      </div>
    </>
  );
};

export default InventoryPartial;
